/*
 * OBX Airwaves — Outer Banks Surf Report (NWS Newport/Morehead City SRFMHX)
 * Source: https://obxairwaves.com/weatherdata/surf.json
 * Only the NWS forecast collector supplies data. No direct client calls to api.weather.gov.
 * Custom display ID 14 (Marine=12, Tides=13). Disabled until user enables it.
 */
import STATUS from '../../modules/status.mjs';
import WeatherDisplay from '../../modules/weatherdisplay.mjs';
import { registerDisplay } from '../../modules/navigation.mjs';

const DATA_URL = 'https://obxairwaves.com/weatherdata/surf.json';
const MAX_RETRIEVAL_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_ISSUE_AGE_MS = 36 * 60 * 60 * 1000;
const FETCH_TIMEOUT_MS = 12000;
const TEN_MIN_MS = 10 * 60 * 1000;
const ZONES = [
    { id: 'NCZ203', name: 'NORTHERN OBX' },
    { id: 'NCZ205-N', name: 'HATTERAS N.' },
    { id: 'NCZ205-S', name: 'HATTERAS S.' },
    { id: 'NCZ204', name: 'OCRACOKE' },
];
const RISK_VALUES = new Set(['Low', 'Moderate', 'High']);

function easternDate(ms) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date(ms));
    const value = (type) => parts.find((p) => p.type === type)?.value ?? '';
    return `${value('year')}-${value('month')}-${value('day')}`;
}

function nextEasternDate(dateString) {
    // Noon UTC avoids crossing either Eastern DST boundary.
    return easternDate(Date.parse(`${dateString}T12:00:00Z`) + 24 * 60 * 60 * 1000);
}

function reportDate(dateString) {
    const ms = Date.parse(`${dateString}T12:00:00Z`);
    if (!Number.isFinite(ms)) return '--';
    return new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric',
    }).format(new Date(ms)).toUpperCase();
}

function shortSurf(input) {
    if (typeof input !== 'string' || !input.trim()) return '--';
    const text = input.trim().replace(/\s+/g, ' ');
    const range = text.match(/^(\d+(?:\.\d+)?)\s+to\s+(\d+(?:\.\d+)?)\s+(?:feet|foot|ft)\b/i);
    if (range) return `${range[1]}-${range[2]} FT`;
    const single = text.match(/^(?:(?:around|about|up to|near)\s+)?(\d+(?:\.\d+)?)\s+(?:feet|foot|ft)\b/i);
    if (single) return `${single[1]} FT`;
    return text.toUpperCase();
}

function shortWater(input) {
    if (typeof input !== 'string' || !input.trim()) return '--';
    return input.trim().replace(/^in\s+the\s+/i, '').toUpperCase();
}

function shortWind(input) {
    if (typeof input !== 'string' || !input.trim()) return '--';
    let value = input.trim().replace(/\s+/g, ' ');
    const directions = {
        northwest: 'NW', northeast: 'NE', southwest: 'SW', southeast: 'SE',
        north: 'N', south: 'S', east: 'E', west: 'W', variable: 'VRB',
    };
    value = value.replace(/^(northwest|northeast|southwest|southeast|north|south|east|west|variable)\b/i,
        (v) => directions[v.toLowerCase()]);
    value = value.replace(/\bwinds?\b/gi, '').replace(/\baround\b/gi, '').replace(/\bto\b(?=\s*\d)/gi, '-');
    value = value.replace(/\s*-\s*/g, '-').replace(/\s+/g, ' ').trim();
    return value.toUpperCase();
}

function safeText(value) {
    return typeof value === 'string' && value.trim().length > 0 && value.length <= 150;
}

function setText(container, selector, value) {
    const element = container.querySelector(selector);
    if (element) element.textContent = value;
}

class ObxSurf extends WeatherDisplay {
    constructor() {
        super(14, 'obx-surf', 'OBX Surf Report', false);
        this.refreshTime = 30 * 60 * 1000;
        this.timing.totalScreens = 1;
        this.timing.baseDelay = 11000;
        this.timing.delay = 1;
    }

    async getData(weatherParameters, refresh = false) {
        if (!super.getData(weatherParameters, refresh)) return;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
        try {
            const response = await fetch(`${DATA_URL}?nocache=${Date.now()}`,
                { mode: 'cors', cache: 'no-store', signal: controller.signal });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const doc = await response.json();
            if (doc?.schema_version !== 1 || doc.timezone !== 'America/New_York'
                || !Array.isArray(doc.zones) || doc.zones.length < 3 || doc.zones.length > 4) {
                throw new Error('Unexpected surf.json schema');
            }
            const now = Date.now();
            const retrieved = Date.parse(doc.retrieved_at_utc);
            const issued = Date.parse(doc.issued_at_utc);
            if (!Number.isFinite(retrieved) || !Number.isFinite(issued)
                || retrieved > now + TEN_MIN_MS || issued > now + TEN_MIN_MS
                || issued > retrieved + TEN_MIN_MS
                || now - retrieved > MAX_RETRIEVAL_AGE_MS || now - issued > MAX_ISSUE_AGE_MS) {
                throw new Error('NWS Surf Zone Forecast missing, outdated or future-dated');
            }
            const today = easternDate(now);
            const tomorrow = nextEasternDate(today);
            const allowed = new Set([today, tomorrow]);
            const rows = new Map();
            for (const zone of doc.zones) {
                if (!zone || rows.has(zone.id) || !ZONES.some((item) => item.id === zone.id)
                    || !allowed.has(zone.forecast_date_local)) continue;
                const risk = RISK_VALUES.has(zone.risk) ? zone.risk : null;
                const surf = safeText(zone.surf_height) ? zone.surf_height.trim() : null;
                if (risk && surf) rows.set(zone.id, {
                    risk, surf, forecastDate: zone.forecast_date_local,
                    water: safeText(zone.water_temp) ? zone.water_temp.trim() : null,
                    wind: safeText(zone.wind) ? zone.wind.trim() : null,
                });
            }
            if (rows.size < 3) throw new Error('Too few usable forecasts for four-area report');
            this.data = rows;
            this.timing.totalScreens = 1;
            this.calcNavTiming();
            this.getDataCallback();
            this.setStatus(STATUS.loaded);
        } catch (error) {
            this.data = undefined;
            this.timing.totalScreens = 0;
            this.calcNavTiming();
            this.getDataCallback();
            this.setStatus(STATUS.noData);
            console.warn('OBX Surf Report skipped:', error?.message ?? error);
        } finally {
            clearTimeout(timeout);
        }
    }

    drawCanvas() {
        super.drawCanvas();
        if (!this.elem || !this.data) { this.finishDraw(); return; }
        const dates = [...new Set([...this.data.values()].map((row) => row.forecastDate))];
        const dateLabel = dates.length === 1 ? reportDate(dates[0]) : 'LOCAL FORECAST';
        setText(this.elem, '.obx-surf-report-date', dateLabel);
        for (const zone of ZONES) {
            const element = this.elem.querySelector(`[data-surf-zone="${zone.id}"]`);
            if (!element) continue;
            const item = this.data.get(zone.id);
            setText(element, '.obx-surf-risk', item?.risk?.toUpperCase() ?? 'N/A');
            setText(element, '.obx-surf-height', item ? shortSurf(item.surf) : '--');
            setText(element, '.obx-surf-water', item ? shortWater(item.water) : '--');
            setText(element, '.obx-surf-wind-value', item ? shortWind(item.wind) : '--');
            const risk = element.querySelector('.obx-surf-risk');
            risk?.classList.remove('obx-surf-low', 'obx-surf-moderate', 'obx-surf-high');
            if (item && risk) risk.classList.add(`obx-surf-${item.risk.toLowerCase()}`);
            element.classList.toggle('obx-surf-unavailable', !item);
        }
        this.finishDraw();
    }
}

registerDisplay(new ObxSurf());
